import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Actions, ofActionCompleted, ofActionDispatched, ofActionSuccessful, Select, Store } from '@ngxs/store';
import { OnDestroyMixin, untilComponentDestroyed } from '@w11k/ngx-componentdestroyed';
import { filter, noop, Observable, tap } from 'rxjs';
import { MatLegacyDialog as MatDialog } from '@angular/material/legacy-dialog';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MatLegacyTabChangeEvent as MatTabChangeEvent } from '@angular/material/legacy-tabs';

import { TsSnackbarService } from 'tsui';
import { CustomObject } from 'app/shared/modules/custom-objects/custom-objects.types';
import { ObjectsManagerActions } from '../state/actions';
import { ObjectsManagerState } from '../state/state';
import { ObjectLink } from '../objects-manager.types';
import { AddRelationshipToObjectComponent } from '../modals/add-relationship-to-object/add-relationship-to-object.component';
import { FieldDef } from 'app/core/models';
import { AddObjectComponent } from '../modals/add-object/add-object.component';
import { ConfirmService } from 'app/shared/components/widgets/dialog/confirm/confirm.service';
import { delay, switchMap } from 'rxjs/operators';

@Component({
  selector: 'objects-manager-details',
  templateUrl: './details.component.html',
  styleUrls: ['./details.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ObjectsManagerDetailsComponent extends OnDestroyMixin implements OnInit {
  customObject: CustomObject;
  linksDisplayedColumns = ['linked_type', 'relationship_type'];
  linksDataSource: ObjectLink[];
  selectedTabLabel: string;

  configurationsForm: FormGroup;
  searchCustomFieldOptions: FieldDef[];
  isLoading = false;

  @Select(ObjectsManagerState.getById) getById$: Observable<CustomObject>;
  @Select(ObjectsManagerState.get) customObjects$: Observable<CustomObject[]>;

  public get isSaveBtnDisabled(): boolean {
    return this.isLoading || this.configurationsForm.pristine || this.configurationsForm.invalid;
  }

  constructor(
    private readonly activatedRoute: ActivatedRoute,
    private readonly changeDetectorRef: ChangeDetectorRef,
    private readonly router: Router,
    private readonly matDialog: MatDialog,
    private readonly snackBar: TsSnackbarService,
    private readonly formBuilder: FormBuilder,
    private readonly store: Store,
    private readonly actions$: Actions,
    private readonly confirmService: ConfirmService,
  ) {
    super();

    this.selectedTabLabel = 'Relationships';
  }

  ngOnInit(): void {
    this.buildForm();

    this.store.dispatch(new ObjectsManagerActions.Get());

    this.actions$.pipe(untilComponentDestroyed(this), ofActionDispatched(ObjectsManagerActions.Patch)).subscribe(() => {
      this.isLoading = true;
      this.changeDetectorRef.markForCheck();
    });

    this.actions$.pipe(untilComponentDestroyed(this), ofActionSuccessful(ObjectsManagerActions.Patch)).subscribe(() => {
      this.isLoading = false;
      this.snackBar.open('Object updated successfully', 'success');
      this.changeDetectorRef.markForCheck();
    });

    this.actions$
      .pipe(untilComponentDestroyed(this), ofActionSuccessful(ObjectsManagerActions.Delete), delay(1000))
      .subscribe(() => {
        this.snackBar.open('Object deleted successfully', 'success');
      });

    this.actions$.pipe(untilComponentDestroyed(this), ofActionCompleted(ObjectsManagerActions.Delete)).subscribe(() => {
      this.router.navigate(['settings', 'objects_manager']);
    });

    this.store.dispatch(new ObjectsManagerActions.GetById(this.activatedRoute.snapshot.params.id));

    this.getById$
      .pipe(
        untilComponentDestroyed(this),
        tap((customObject) => {
          this.customObject = customObject;
          this.linksDataSource = customObject?.links || [];
          this.searchCustomFieldOptions =
            (customObject?.custom_fields ?? customObject?.fields)?.filter((field) => !field.archived) || [];
          this.configurationsForm.patchValue({ search_custom_field_id: customObject?.search_custom_field_id });
          this.isLoading = false;
          this.changeDetectorRef.markForCheck();
        }),
      )
      .subscribe(noop);
  }

  navigateToCustomFields(): void {
    this.router.navigate(['settings', 'custom_fields', this.customObject.name]);
  }

  addRelationshipToObject(): void {
    this.store
      .dispatch(new ObjectsManagerActions.Get())
      .subscribe(() => this.matDialog.open(AddRelationshipToObjectComponent));
  }

  private buildForm(): void {
    this.configurationsForm = this.formBuilder.group({
      search_custom_field_id: [null, [Validators.required]],
    });
  }

  selectedTabChange(event: MatTabChangeEvent): void {
    this.selectedTabLabel = event.tab.textLabel;
  }

  save(): void {
    this.store.dispatch(new ObjectsManagerActions.Patch(this.customObject.id, this.configurationsForm.value));
  }

  isDebounceLoading(isLoading: boolean): boolean {
    return (this.isLoading = isLoading);
  }

  deleteCustomObject(): void {
    this.confirmService
      .openConfirmationModal()
      .pipe(
        filter((result: boolean) => result),
        switchMap(() => this.store.dispatch(new ObjectsManagerActions.Delete(this.customObject.id))),
      )
      .subscribe(noop);
  }

  editCustomObject(): void {
    this.matDialog.open(AddObjectComponent, {
      panelClass: 'mailbox-compose-dialog',
      data: { customObject: this.customObject },
    });
  }

  getLabelForLinked(link: ObjectLink, customObjects: CustomObject[]): string {
    return customObjects.find(({ name }) => name === link.linked_type)?.label ?? link.linked_type;
  }
}
